/**
 * Token 管理工具
 * 用于处理双 token（accessToken + refreshToken）的存储、获取和刷新
 */

interface TokenInfo {
  accessToken: string;
  refreshToken: string;
  accessExpiresAt: number; // 过期时间戳（毫秒）
  refreshExpiresAt: number; // 过期时间戳（毫秒）
}

const TOKEN_KEY = 'token_info';

/**
 * 保存 token 信息
 * @param tokens - 包含 token 和过期时间的对象
 */
export function saveTokens(tokens: {
  accessToken: string;
  refreshToken: string;
  accessExpiresIn: number; // 秒
  refreshExpiresIn: number; // 秒
}) {
  const now = Date.now();
  const tokenInfo: TokenInfo = {
    accessToken: tokens.accessToken,
    refreshToken: tokens.refreshToken,
    // 减去30秒作为缓冲，提前刷新
    accessExpiresAt: now + (tokens.accessExpiresIn - 30) * 1000,
    refreshExpiresAt: now + tokens.refreshExpiresIn * 1000,
  };
  localStorage.setItem(TOKEN_KEY, JSON.stringify(tokenInfo));
  // 兼容旧代码，保留 access_token
  localStorage.setItem('access_token', tokens.accessToken);
}

/**
 * 获取完整的 token 信息
 */
export function getTokenInfo(): TokenInfo | null {
  const tokenStr = localStorage.getItem(TOKEN_KEY);
  if (!tokenStr) {
    return null;
  }
  try {
    return JSON.parse(tokenStr) as TokenInfo;
  } catch (error) {
    console.error('解析 token 信息失败:', error);
    return null;
  }
}

/**
 * 获取 access token
 */
export function getAccessToken(): string | null {
  const tokenInfo = getTokenInfo();
  return tokenInfo?.accessToken || null;
}

/**
 * 获取 refresh token
 */
export function getRefreshToken(): string | null {
  const tokenInfo = getTokenInfo();
  return tokenInfo?.refreshToken || null;
}

/**
 * 检查 access token 是否已过期
 */
export function isAccessTokenExpired(): boolean {
  const tokenInfo = getTokenInfo();
  if (!tokenInfo) {
    return true;
  }
  return Date.now() >= tokenInfo.accessExpiresAt;
}

/**
 * 检查 refresh token 是否已过期
 */
export function isRefreshTokenExpired(): boolean {
  const tokenInfo = getTokenInfo();
  if (!tokenInfo) {
    return true;
  }
  return Date.now() >= tokenInfo.refreshExpiresAt;
}

/**
 * 检查是否有有效的 token
 */
export function hasValidToken(): boolean {
  return !isRefreshTokenExpired();
}

/**
 * 更新 access token（用于刷新后）
 */
export function updateAccessToken(accessToken: string, accessExpiresIn: number) {
  const tokenInfo = getTokenInfo();
  if (!tokenInfo) {
    return;
  }
  const now = Date.now();
  tokenInfo.accessToken = accessToken;
  tokenInfo.accessExpiresAt = now + (accessExpiresIn - 30) * 1000;
  localStorage.setItem(TOKEN_KEY, JSON.stringify(tokenInfo));
  // 兼容旧代码
  localStorage.setItem('access_token', accessToken);
}

/**
 * 清除所有 token
 */
export function clearTokens() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem('access_token');
}
